import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, ArrowRight, CalendarClock, CheckCircle2, XCircle, MapPin, Upload, ClipboardCheck, FileText, Wallet, Hourglass } from "lucide-react";
import { todayISO, getCurrentLocation, fmtDate, fmtTime, errorMsg } from "@/lib/utils-app";
import { useGpsTrail } from "@/lib/useGpsTrail";
import { HourlySelfieBanner } from "@/components/pmc/HourlySelfieBanner";
import { MyWeeklySchedule } from "@/components/MyWeeklySchedule";
import { toast } from "sonner";

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span data-testid="live-clock" className="tabular-nums">
      {fmtDate(now)} · {fmtTime(now)} IST
    </span>
  );
}

export default function PmcTodayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [visitMap, setVisitMap] = useState({}); // park_id -> { status, reason }
  // Cleaning checklist status per park today, from /pmc/cleaning-checklists/today
  // — { [park_id]: { before, after } }. Used to remind the supervisor that a
  // pending After-cleaning at another park is still saved and waiting.
  const [cleaningByPark, setCleaningByPark] = useState({});
  const [busy, setBusy] = useState({}); // park_id -> bool
  // Today's admin-pinned schedule. We use this to (a) badge scheduled
  // parks visually and (b) warn the supervisor with an AlertDialog
  // when they try to capture attendance / open cleaning at a park
  // that is NOT on today's schedule. The capture is still allowed —
  // we just attach a flag so the admin can spot the deviation.
  const [scheduledParkIds, setScheduledParkIds] = useState(null); // null = no schedule pinned, Set = present
  const [confirmDialog, setConfirmDialog] = useState(null); // { kind, parkName, onConfirm }

  // Foreground GPS pings every 5 min for movement trail
  useGpsTrail({ enabled: true });

  const refresh = () => {
    let alive = true;
    setLoading(true);
    api
      .get("/dashboard/pmc")
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));
    api
      .get("/work-plans", { params: { start: todayISO(), end: todayISO() } })
      .then((r) => alive && setPlans(r.data));
    api
      .get("/park-visit-status/today")
      .then((r) => alive && setVisitMap(r.data?.by_park || {}));
    api
      .get("/pmc/cleaning-checklists/today")
      .then((r) => alive && setCleaningByPark(r.data?.by_park || {}))
      .catch(() => {});
    // Pull today's schedule so we can both render badges and gate the
    // attendance/cleaning entry points with an off-schedule warning.
    api
      .get("/me/visit-schedule")
      .then((r) => {
        if (!alive) return;
        const today = r.data?.today_parks || [];
        const byDay = r.data?.by_day || {};
        const anyScheduleAtAll = today.length > 0 || Object.values(byDay).some((arr) => arr.length > 0);
        if (!anyScheduleAtAll) { setScheduledParkIds(null); return; } // no schedule pinned
        setScheduledParkIds(new Set(today.map((p) => p.park_id)));
      })
      .catch(() => setScheduledParkIds(null));
    return () => { alive = false; };
  };

  useEffect(() => {
    refresh();
  }, []);

  const setStatus = async (parkId, status) => {
    setBusy((b) => ({ ...b, [parkId]: true }));
    try {
      const loc = status === "visited" ? await getCurrentLocation() : null;
      const { data: rec } = await api.post("/park-visit-status", {
        park_id: parkId,
        status,
        gps_lat: loc?.lat || null,
        gps_long: loc?.long || null,
      });
      setVisitMap((m) => ({ ...m, [parkId]: rec }));
      toast.success(status === "visited" ? "Marked as visited" : "Marked as not visited");
    } catch (e) {
      toast.error(errorMsg(e, "Failed to update"));
    } finally {
      setBusy((b) => ({ ...b, [parkId]: false }));
    }
  };

  if (loading || !data)
    return (
      <AppShell title="Today">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </AppShell>
    );

  return (
    <AppShell
      title={<LiveClock />}
      subtitle="Mark each park as visited or not visited, then capture attendance"
      action={
        <div className="flex flex-wrap items-center gap-1.5">
          <Link to="/pmc/salary-verification" data-testid="pmc-go-salary-verification" aria-label="Salary Verification">
            <Button variant="outline" size="sm" className="h-9 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 px-2 sm:px-3">
              <Wallet className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Salary</span>
            </Button>
          </Link>
          <Link to="/pmc/feedback" data-testid="pmc-go-feedback" aria-label="Monthly Feedback">
            <Button variant="outline" size="sm" className="h-9 border-indigo-300 bg-indigo-50 text-indigo-900 hover:bg-indigo-100 px-2 sm:px-3">
              <FileText className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Feedback</span>
            </Button>
          </Link>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <HourlySelfieBanner />
        <MyWeeklySchedule />
        {data.parks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No parks assigned to you yet.
            </CardContent>
          </Card>
        ) : (
          data.parks.map((row) => {
            const pParks = plans.filter((p) => p.park_id === row.park.id);
            const total = row.employee_count || 0;
            const done = row.attendance_marked_today || 0;
            const remaining = Math.max(0, total - done);
            const vs = visitMap[row.park.id] || null;
            const isVisited = vs?.status === "visited";
            const isNotVisited = vs?.status === "not_visited";
            const undecided = !vs;
            // Before submitted but After still pending — the checklist stays
            // saved and available; this just reminds the supervisor it's
            // waiting to be finished.
            const cleaning = cleaningByPark[row.park.id];
            const afterPending = !!cleaning?.before && !cleaning?.after;
            // Card colour theme per visit status
            const cardTheme = isVisited
              ? "border-emerald-300 bg-emerald-50/60"
              : isNotVisited
              ? "border-amber-300 bg-amber-50/60"
              : "border-slate-200 bg-white";
            return (
              <Card
                key={row.park.id}
                className={cardTheme}
                data-testid={`pmc-park-${row.park.code}`}
              >
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-base truncate">{row.park.name}</CardTitle>
                      {isVisited && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white" data-testid={`pmc-park-${row.park.code}-visited`}>
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Visited
                        </Badge>
                      )}
                      {isNotVisited && (
                        <Badge className="bg-amber-500 hover:bg-amber-500 text-white" data-testid={`pmc-park-${row.park.code}-not-visited`}>
                          <XCircle className="mr-1 h-3 w-3" /> Not visited
                        </Badge>
                      )}
                      {afterPending && (
                        <Badge
                          className="bg-amber-500 hover:bg-amber-500 text-white"
                          data-testid={`pmc-park-${row.park.code}-after-pending`}
                          title="Before-cleaning was submitted; After-cleaning is still pending and saved for whenever you finish it."
                        >
                          <Hourglass className="mr-1 h-3 w-3" /> After-cleaning pending
                        </Badge>
                      )}
                      {scheduledParkIds && scheduledParkIds.has(row.park.id) && isNotVisited ? (
                        <Badge className="animate-pulse bg-sky-600 hover:bg-sky-600 text-white" data-testid={`pmc-park-${row.park.code}-scheduled-reminder`}>
                          <CalendarClock className="mr-1 h-3 w-3" /> Today's visit pending
                        </Badge>
                      ) : null}
                      {/* Capture counts intentionally hidden from supervisors. */}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      <span className="font-mono">{row.park.code}</span> · {row.park.district || "—"}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {pParks.length > 0 && (
                      <span className="text-muted-foreground">{pParks.length} planned items</span>
                    )}
                  </div>
                  {pParks.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {pParks.slice(0, 4).map((p) => (
                        <Badge
                          key={p.id}
                          variant={p.overridden_location ? "outline" : "secondary"}
                          className={p.overridden_location ? "border-amber-400 bg-amber-50 text-amber-900" : ""}
                        >
                          <CalendarClock className="mr-1 h-3 w-3" />
                          <span className="uppercase font-mono mr-1 text-muted-foreground">
                            {p.work_type}
                          </span>
                          {p.overridden_location || p.location}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {undecided ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                        onClick={() => setStatus(row.park.id, "visited")}
                        disabled={busy[row.park.id]}
                        data-testid={`pmc-mark-visited-${row.park.code}`}
                      >
                        <MapPin className="mr-2 h-4 w-4" /> Visited
                      </Button>
                      <Button
                        variant="outline"
                        className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                        onClick={() => setStatus(row.park.id, "not_visited")}
                        disabled={busy[row.park.id]}
                        data-testid={`pmc-mark-not-visited-${row.park.code}`}
                      >
                        <XCircle className="mr-2 h-4 w-4" /> Not visited
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Link
                        to={`/pmc/attendance?park=${row.park.id}${isNotVisited ? "&upload=1" : ""}`}
                        data-testid={`pmc-go-attendance-${row.park.code}`}
                      >
                        <Button className="w-full">
                          {isNotVisited ? (
                            <>
                              <Upload className="mr-2 h-4 w-4" /> Attendance Verification
                            </>
                          ) : (
                            <>
                              <Camera className="mr-2 h-4 w-4" /> Open attendance
                            </>
                          )}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link
                        to={`/pmc/cleaning?park=${row.park.id}&mode=before`}
                        data-testid={`pmc-go-cleaning-${row.park.code}`}
                      >
                        <Button variant="outline" className="mt-2 w-full border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100">
                          <ClipboardCheck className="mr-2 h-4 w-4" /> Cleaning checklist
                        </Button>
                      </Link>
                      <button
                        type="button"
                        className="mt-2 w-full text-[11px] text-muted-foreground underline hover:text-primary"
                        onClick={() => setStatus(row.park.id, isNotVisited ? "visited" : "not_visited")}
                        disabled={busy[row.park.id]}
                        data-testid={`pmc-toggle-visit-${row.park.code}`}
                      >
                        Change to {isNotVisited ? "Visited" : "Not visited"}
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <AlertDialog open={false}>
        <AlertDialogContent></AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
